import React, { useEffect, useState } from 'react';
import { Card, Table, Tabs, Tag, Typography, Space, Button, Input, message, Empty, Spin } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import agentConfigService from '../services/agentConfigService';

const { Text } = Typography;

const fmtMs = (ms) => (ms == null ? '—' : `${Math.round(ms)} ms`);
const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString());
const fmtConf = (c) => (c == null ? '—' : `${(c * 100).toFixed(1)}%`);

function StatsTable({ rows, loading }) {
  return (
    <Table
      size="small"
      loading={loading}
      rowKey={(r) => r.prompt_version || '(none)'}
      dataSource={rows}
      pagination={{ pageSize: 20 }}
      columns={[
        { title: 'Prompt version', dataIndex: 'prompt_version', render: (v) => v || <Text type="secondary">(none)</Text> },
        { title: 'Runs', dataIndex: 'run_count', render: fmtNum, width: 100 },
        { title: 'Avg confidence', dataIndex: 'avg_confidence', render: fmtConf, width: 160 },
        { title: 'Avg latency', dataIndex: 'avg_latency_ms', render: fmtMs, width: 140 },
        { title: 'Errors', dataIndex: 'error_count', render: fmtNum, width: 100 },
        { title: 'Last seen', dataIndex: 'last_seen_at', width: 220 },
      ]}
    />
  );
}

function RunsTable({ rows, loading }) {
  return (
    <Table
      size="small"
      loading={loading}
      rowKey={(r) => r.id}
      dataSource={rows}
      pagination={{ pageSize: 50 }}
      expandable={{
        expandedRowRender: (r) => (
          <div style={{ padding: 8, background: '#fafafa' }}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>SQL:</Text>{' '}
              <pre style={{ display: 'inline', fontSize: 12, background: '#fff', padding: 4, borderRadius: 4 }}>
                {r.sql || '(none)'}
              </pre>
            </div>
            {r.confidence_breakdown && (
              <details>
                <summary>Confidence breakdown</summary>
                <pre style={{ fontSize: 12 }}>{JSON.stringify(r.confidence_breakdown, null, 2)}</pre>
              </details>
            )}
            {r.tools_used && (
              <details>
                <summary>Tools used ({(r.tools_used || []).length})</summary>
                <pre style={{ fontSize: 12 }}>{JSON.stringify(r.tools_used, null, 2)}</pre>
              </details>
            )}
            {r.sanity_warnings && (
              <details open>
                <summary>Sanity warnings</summary>
                <pre style={{ fontSize: 12 }}>{JSON.stringify(r.sanity_warnings, null, 2)}</pre>
              </details>
            )}
          </div>
        ),
      }}
      columns={[
        { title: 'Time', dataIndex: 'created_at', width: 200 },
        { title: 'User', dataIndex: 'user_email', width: 200, ellipsis: true },
        { title: 'Prompt v', dataIndex: 'prompt_version', width: 130, render: (v) => <Tag>{v || '—'}</Tag> },
        { title: 'Model', dataIndex: 'model_key', width: 160 },
        { title: 'Intent', dataIndex: 'intent', width: 100 },
        { title: 'Iter', dataIndex: 'iterations', width: 60 },
        { title: 'Conf', dataIndex: 'confidence_score', width: 90, render: fmtConf },
        { title: 'Rows', dataIndex: 'row_count', width: 80, render: fmtNum },
        { title: 'Latency', dataIndex: 'latency_ms', width: 110, render: fmtMs },
        { title: 'Tokens (in/out)', width: 140, render: (_, r) => `${fmtNum(r.input_tokens)} / ${fmtNum(r.output_tokens)}` },
        { title: 'Error', dataIndex: 'error_kind', width: 140, render: (v) => v ? <Tag color="red">{v}</Tag> : <span style={{ color: '#aaa' }}>—</span> },
        { title: 'Question', dataIndex: 'user_query', ellipsis: true },
      ]}
    />
  );
}

const DataAgentTelemetry = () => {
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [filter, setFilter] = useState({ promptVersion: '', userEmail: '' });

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const res = await agentConfigService.getTelemetryStats(isAdmin, 50);
      setStats(res.items || []);
    } catch (e) {
      message.error(`Failed to load stats: ${e.message || e}`);
    } finally {
      setLoadingStats(false);
    }
  };

  const loadRuns = async () => {
    setLoadingRuns(true);
    try {
      const res = await agentConfigService.getTelemetryRuns(isAdmin, {
        limit: 200,
        promptVersion: filter.promptVersion || undefined,
        userEmail: filter.userEmail || undefined,
      });
      setRuns(res.items || []);
    } catch (e) {
      message.error(`Failed to load runs: ${e.message || e}`);
    } finally {
      setLoadingRuns(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    loadStats();
    loadRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div style={{ padding: 24 }}>
        <Empty description="Admin access required" />
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <Card
        title="Data Agent Telemetry"
        extra={
          <Space>
            <Input
              placeholder="prompt_version"
              value={filter.promptVersion}
              onChange={(e) => setFilter((f) => ({ ...f, promptVersion: e.target.value }))}
              style={{ width: 180 }}
              allowClear
            />
            <Input
              placeholder="user_email"
              value={filter.userEmail}
              onChange={(e) => setFilter((f) => ({ ...f, userEmail: e.target.value }))}
              style={{ width: 200 }}
              allowClear
            />
            <Button icon={<ReloadOutlined />} onClick={() => { loadStats(); loadRuns(); }}>
              Refresh
            </Button>
          </Space>
        }
      >
        <Tabs
          items={[
            {
              key: 'stats',
              label: 'By prompt version',
              children: <StatsTable rows={stats} loading={loadingStats} />,
            },
            {
              key: 'runs',
              label: `Recent runs (${runs.length})`,
              children: <RunsTable rows={runs} loading={loadingRuns} />,
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default DataAgentTelemetry;
